import { anyReallyAny } from "../../tools";
import { ValueOrFunction } from "../tools/object.tools";
import { FilterOptions } from "../tools/string.tools";
import { DataEmptyValue, DataSourceItem, DataSourceUID } from "./dataSource";
import { PropertyConfigType } from "./propertyConfig.types";
import { ValidationConstraints } from "./validationError";

/**
 * Configuration of a property config to generate.
 */
export interface PropertyConfig<T = PropertyConfigType, V extends PropertyOptions = PropertyOptions> {
    propertyName: string;
    label: string;
    typeUid: T;
    options?: V;
    validators?: ValidationConstraints;
}

/**
 * Generic property options.
 */
export interface PropertyOptions {

    /** 
     * Add a tooltip in editor if exist 
     * Using htmlString will use bootstrap tooltip with html enabled
     */
    tooltip?: string | { htmlString: string };

    /** Do not add tooltip icon */
    doNotShowTooltipIcon?: boolean;

    /** Add a placeholder in input if exist */
    placeholder?: string;

    /** Add a clear button in input if exist */
    clearButton?: boolean;

    /** Prefix text */
    prefix?: string;

    /** Suffix text */
    suffix?: string;

    /** Disable editor */
    disabled?: ValueOrFunction<boolean>;

    /** Hide editor */
    hidden?: ValueOrFunction<boolean>;

    /** Default editor value when initialized */
    defaultValue?: ValueOrFunction<unknown>;

    /** Section of editor */
    section?: PropertyLayoutSection;

    /** Layout of property, only used in generatedForm */
    layout?: {

        /** Number of cols where the property will expand */
        colSpan?: number,

        /** Should add a line break after property */
        appendLineBreak?: boolean,

        /** Display form label in horizontal or vertical alignment*/
        labelLayout?: PropertyLabelLayout,

        /**
         * Width of labels, used in multiple columns when colSpan is given
         * (in px)
         */
        labelWidth?: number
    };

}

/**
 * Property options for EditorWithPicker
 */
export interface PropertyOptionsEditorWithPicker extends PropertyOptions {
    picker?: {
        /**
         * "default" or "direct"
         * @see EditorWithPickerPopupMode
         */
        popupMode?: string,

        /**
         * TODO
         */
        only?: boolean
    };
}

export interface PropertyOptionsTextArea extends PropertyOptions {
    /** text area row count */
    rows?: number;
}

export interface PropertyOptionsMinutesIntervals extends PropertyOptions {
    /** 
     * Convert only for 24h "day" duration: 
     * formatMinutesHHMM(1520) => "25:20" 
     * formatMinutesHHMM(1520, {dayFormat: true}) => "01:20" 
     * */
    dayFormat?: boolean;
}

/**
 * Property options for checkbox group
 */
export interface PropertyOptionsCheckboxGroup extends PropertyOptions {
    /**
     * List of items for radios options
     */
    itemList: ValueOrFunction<string> | DataSourceUID;
}

/**
 * Property options for lists
 */
export interface PropertyOptionsList extends PropertyOptions, FilterOptions<anyReallyAny> {
    /**
     * ! ValueOrFunction<anyReallyAny[]> is deprecated. Please use a DataSourceUID when using a select editor.
     */
    itemList: ValueOrFunction<anyReallyAny[]> | DataSourceUID;

    /**
     * Compute display string for item
     * ! This option is valid only if you provided itemList as an array (ie not a data source)
     */
    itemDisplayString?: (value: string | number, itemFromCache?: DataSourceItem<anyReallyAny>) => string;

    /** Empty value added to the list
     * ! Only handled by the PropertyConfigType.LISTSELECT for now
     */
    emptyValue?: DataEmptyValue<null>;


    /** Options for multiselect */
    multiSelect?: {

        /** Enable Multi-selection behavior for LISTSELECTs */
        enable: ValueOrFunction<boolean>,

        /** All items will be selected when select is init */
        selectAllOnInit?: boolean

    };

    /** Comparator used to filter on items list */
    filter?: (item: DataSourceItem<anyReallyAny> | anyReallyAny, filterValue: anyReallyAny) => boolean;
}

export interface PropertyOptionsRange extends PropertyOptions {
    /**
     * Max range value
     */
    max?: number;
    /**
     * Min range value
     */
    min?: number;
    /**
     * Incremental step between each range value
     */
    step?: number;
    /**
     * True if we want to display range value
     */
    displayValue?: boolean;
}

export interface PropertyOptionsDuration extends PropertyOptions {

    pickerOptions?: {
        /** Enable duration selectors for hours and minutes */
        enableDurationSelectors: boolean,

        /** Use hours, minutes and days */
        useDays?: boolean,

        /** use seconds value */
        useSeconds?: boolean,

        /** Allow negative */
        allowNegative?: boolean,

        /** Display selector header units */
        displaySelectorHeaderUnits?: boolean,

        /** Display duration header units */
        displayHeaderUnits?: boolean,

        /** Duration in minutes by default 0*/
        durationInMinutes?: number,

        /** Add range to duration editor */
        ranges?: [
            {
                /** Duration start in minutes */
                startDuration: number,

                /** Duration end in minutes */
                finishDuration: number
            }
        ]
    };
}

/**
 * Property config options for time editors
 */
export interface PropertyOptionsTime extends PropertyOptions {
    /** Picker options */
    pickerOptions?: {

        /** Max positive hours limit */
        maxHoursLimit?: number,

        /** Min negative hours limit */
        minNegativeHoursLimit?: number
    };
}

/**
 * Regroup all PropertyOptions to ease typings in your project. 
 * If you add PropertyOptions in cervval.js add your options here. 
 * If you extend PropertyOptions in your project, you can define your own 
 * type : `type ProjectPropertyOptionsAll = ProjectPropertyOptions | ProjectPropertyOptionsAll;`.
 * You can also define a type alias in your project 
 * (eg. `export type PropertyConfigGeneric = PropertyConfig<PropertyConfigType, PropertyOptionsAll>;`).
 */
export type PropertyOptionsAll = PropertyOptions | PropertyOptionsList | PropertyOptionsEditorWithPicker | PropertyOptionsRange | PropertyOptionsDuration | PropertyOptionsTextArea | PropertyOptionsTime;

/**
 * Use to generate section of properties (accordion or tabs)
 */
export interface PropertySection {

    /** Label displayed on accordion */
    label: string;

    /** Order to display accordions */
    displayOrder: number;
}

/**
 * Use to generate accordion to contain properties
 */
export interface PropertyGroup extends PropertySection {

    /** 
     * True : Accordion is collapsed and form items are hidden, need to click on accordion to show form items
     * False : Accordion is not collapsed and form items are visible
     */
    collapsed?: boolean;

    /**
     * ParentId references the parent container. If undefined, parent container is form
     */
    parent?: PropertyGroup;


    /** Prevent the accordion default close behavior when opening another one from same hierarchy level */
    alwaysOpened?: boolean;
}

/**
 * Use to generate tabs to contain properties
 */
export interface PropertyTabs extends PropertySection {

    /** Uid for tab menu */
    uid: string;
}

export interface PropertyLayoutSection {
    /** PropertyConfig group, used to set pc editor in accordions */
    group?: PropertyGroup;
    /** PropertyConfig tab, used to set pc editor in tabs */
    tabs?: PropertyTabs;
}


export type PropertyLabelLayout = "inline-level" | "block-level";


/** 
 * Comparator to use to compare two propertyConfigs 
 * @returns true if propertyName of propertyConfigs are equals
 */
export function propertyConfigComparator(pc1: PropertyConfig, pc2: PropertyConfig): boolean {
    return pc1.propertyName === pc2.propertyName;
}